import {
  and,
  eq,
  getTableColumns,
  getTableName,
  inArray,
  isNull,
  or,
  sql,
  type Table,
} from "drizzle-orm";
import { DrizzleD1Database } from "drizzle-orm/d1";
import { generateUlid } from "@kubdee/shared";

import {
  doctypeRegistry,
  DOCTYPE_FEATURES,
  isDoctypeFeature,
  isDoctypeKind,
  type DoctypeFeature,
  type DoctypeKind,
} from "../schemas/doctype-registry";
import { numberingSeries } from "../schemas/numbering-series";
import { previewPattern } from "./numbering.service";
import {
  normalizeLifecycleContract,
  parseLifecycleContract,
  validateLifecycleContract,
  type DocumentLifecycleContract,
} from "./document-lifecycle";

// The registry schema intentionally carries only (createdAt, updatedAt) — no
// user-attribution columns. Registry writes come from module boot code, not a
// request-authenticated user, so createdBy/updatedBy would always be NULL.
// We inline ISO timestamps instead of using @kubdee/shared `createTimestamps`
// so the field set matches the table shape exactly.
function nowIso(): string {
  return new Date().toISOString();
}

// ============================================================================
// defineDoctype() — Doctype Admin Epic (#347), issue A2 (#349).
//
// Integration pattern (Pattern A — declarative):
// ---------------------------------------------------------------------------
//   defineDoctype(def) is a PURE validator. It takes a caller-supplied
//   definition, normalizes/validates it, and returns a frozen object. It does
//   NOT touch the DB.
//
//   The runtime upsert lives in registerDoctype() / registerDoctypes(), which
//   require a tenant-DB handle and are called by the platform boot sequence
//   (auto-migrate middleware, a future D1 hook — wired separately once A3+
//   land). Splitting validation from the write keeps `defineDoctype()` safe
//   to call inside a module's static `index.ts` manifest (no side effects
//   on import) and matches the established kubdee style where `defineTable`
//   / `defineMigrations` / `defineModule` are all pure helpers and a later
//   runtime step consumes them (e.g. `applyModuleMigrations`).
//
// Scoping:
// ---------------------------------------------------------------------------
//   registerDoctype writes PLATFORM-DEFAULT rows only — `company_id = NULL`.
//   Per-company overrides are created later via explicit user action in the
//   Doctype Admin UI (not this path). The caller API does not expose a
//   companyId parameter on purpose, to prevent accidental writes into
//   tenant-override territory from module boot code.
//
// Idempotence:
// ---------------------------------------------------------------------------
//   The upsert targets the partial unique index
//       uq_doctype_registry_default (id) WHERE company_id IS NULL
//   via Drizzle's `targetWhere`. On conflict, metadata fields are refreshed
//   (label/icon/features/module_id/table_name/tier) so module devs can edit
//   those in code and reboot to see the update. `created_at` is preserved;
//   `updated_at` bumps.
//
//   The registry row is JUST METADATA about the doctype. Per-company
//   customization (custom fields, numbering series, etc.) lives in other
//   tables and is NOT reset by a registry upsert.
// ============================================================================

/**
 * Extract the union of snake_case DB column names from a Drizzle table type.
 *
 * Drizzle stashes each column's runtime DB name in its phantom config:
 * `column._.name`. This walks the table's `_.columns` map and pulls every
 * `name` literal out, producing e.g. `"vehicle_code" | "vehicle_name" | …`.
 *
 * When the caller doesn't pass a `table` to `defineDoctype` (legacy path),
 * `TTable` defaults to `Table`, the column-name resolution falls through,
 * and the field types relax to plain `string` — so existing doctypes that
 * haven't been migrated yet keep type-checking against an opaque schema.
 */
export type ColumnDbNames<TTable extends Table> = TTable extends {
  _: { columns: infer Cols };
}
  ? {
      [K in keyof Cols]: Cols[K] extends { _: { name: infer N extends string } }
        ? N
        : never;
    }[keyof Cols]
  : string;

export const DOCTYPE_CHILD_CASCADE_POLICIES = [
  "cascade",
  "restrict",
  "orphan",
] as const;

export type DoctypeChildCascadePolicy =
  (typeof DOCTYPE_CHILD_CASCADE_POLICIES)[number];

export interface DoctypeChildTableRelation {
  /** Parent doctype id, e.g. `invoice` for `invoice_item`. */
  parentDoctype: string;
  /** Parent form field/section name that owns this child collection. */
  parentField?: string | null;
  /** Column on the child table that stores the parent record id. */
  parentKeyField: string;
  /** Optional child-row ordering column. */
  sortField?: string | null;
  /** What a generic tool should assume when the parent is removed. */
  cascadePolicy?: DoctypeChildCascadePolicy;
}

export interface DoctypeCatalogPolicy {
  isReadOnly?: boolean;
  allowGenericCreate?: boolean;
  allowGenericUpdate?: boolean;
  allowGenericDelete?: boolean;
  isImportable?: boolean;
  isExportable?: boolean;
  isReportable?: boolean;
  isSearchable?: boolean;
}

export interface ResolvedDoctypeCatalogPolicy {
  isReadOnly: boolean;
  allowGenericCreate: boolean;
  allowGenericUpdate: boolean;
  allowGenericDelete: boolean;
  isImportable: boolean;
  isExportable: boolean;
  isReportable: boolean;
  isSearchable: boolean;
}

export interface DoctypeCatalogMetadata {
  childTable?: DoctypeChildTableRelation | null;
  policy?: DoctypeCatalogPolicy | null;
}

export interface ResolvedDoctypeCatalogMetadata {
  childTable: DoctypeChildTableRelation | null;
  policy: ResolvedDoctypeCatalogPolicy;
}

const CATALOG_POLICY_KEYS = [
  "isReadOnly",
  "allowGenericCreate",
  "allowGenericUpdate",
  "allowGenericDelete",
  "isImportable",
  "isExportable",
  "isReportable",
  "isSearchable",
] as const satisfies readonly (keyof DoctypeCatalogPolicy)[];

/**
 * Stored / output shape for a registered doctype — what `defineDoctype()`
 * returns and what the rest of the platform reads. `tableName` is always a
 * string here; the input shape (`DoctypeDefinitionInput`) lets callers
 * either pass it directly or supply a Drizzle `table:` to derive it from.
 *
 * Note that `features` is typed as the narrow `DoctypeFeature[]` union, not
 * `string[]`, so TS rejects unknown capabilities at the call site.
 */
export interface DoctypeDefinition {
  /** Logical doctype id, snake_case — e.g. "quotation", "sales_invoice". */
  id: string;
  /** Module id that owns this doctype — must match the module folder name. */
  moduleId: string;
  /** Physical tenant-DB table name — must be prefixed `{moduleId}_`. */
  tableName: string;
  /** Display label in Thai (UI copy). */
  label: string;
  /** Optional icon — lucide icon name or emoji. */
  icon?: string;
  /** Capability flags the doctype opts into. */
  features: DoctypeFeature[];
  /** Module tier — 0 (platform), 1 (Tier 1), 2 (Tier 2 domain). */
  tier: 0 | 1 | 2;

  /**
   * #644 — ERPNext-style table semantics for the Doctype Catalog. Defaults
   * to `normal`, so existing doctypes stay behaviourally unchanged.
   */
  kind?: DoctypeKind;

  /**
   * #644 — optional child-table relation and generic surface policy. This
   * metadata never enables generic CRUD by itself; generic write surfaces
   * must explicitly check the normalized policy first.
   */
  catalog?: DoctypeCatalogMetadata | null;

  /**
   * #365 — Column that stores the auto-generated identifier.
   *   null      → doctype uses `row.id` (ULID) as the identifier. No
   *               Naming Series pattern is applied on insert.
   *   string    → snake_case column name on `tableName` that receives the
   *               pattern output from `generateNextNumber()` on insert.
   *               Module author picks this at schema-design time.
   *
   * The "naming rule" (ulid vs custom) is DERIVED from this field's
   * presence — we don't store a separate rule column.
   *
   * Examples: "quotation_no", "invoice_no", "partner_code".
   */
  numberingField?: string | null;

  /**
   * #365 — Column to surface when this doctype is referenced elsewhere
   * (list rows, FK dropdowns, search results). Fallback order:
   * displayField → numberingField → row.id.
   *
   * Examples: "partner_name" (partner displays the name, not the code),
   * "quotation_no" (quotation displays its number directly).
   */
  displayField?: string | null;

  /**
   * #365 — Column that holds the record's primary image URL (logo,
   * avatar, thumbnail). Consumed by list avatars, FK dropdowns, detail
   * page headers. null = no image; UI falls back to a generic icon.
   *
   * Examples: "logo_url", "avatar_url", "photo_url".
   */
  imageField?: string | null;

  /**
   * #365 — Columns searched when the user types in global search or an
   * FK picker. Empty/null falls back to `[displayField]`. Each entry
   * must be a snake_case column name on the doctype's physical table.
   *
   * Example: ["party_name", "status", "invoice_no"].
   */
  searchFields?: readonly string[] | null;

  /**
   * #365 follow-up — Thai UI labels for each column on the doctype's
   * table. Keys are snake_case column names; values are the Thai copy
   * the user sees in forms/lists. Surfaces in the Doctype Admin sidebar
   * so admins can read the user-facing label instead of the raw type.
   *
   * Example:
   *   {
   *     quotation_no: "เลขที่ใบเสนอราคา",
   *     party_name:   "ลูกค้า",
   *     grand_total:  "ยอดรวม"
   *   }
   *
   * null/omitted = no labels declared; UI shows the column name only.
   * The admin may override per-tenant via `updateDoctypeMetadata()`.
   */
  columnLabels?: Readonly<Record<string, string>> | null;

  /**
   * Platform-default numbering series seeded when the doctype has the
   * `"naming"` feature. Without this, a fresh tenant's first
   * `generateNextNumber()` call would fail with NOT_FOUND (no series
   * exists yet), and the admin would have to create one by hand before
   * any record could be inserted.
   *
   * `registerDoctype()` upserts this into `core_numbering_series` with
   * `company_id = NULL` — findSeries() already falls back to NULL rows
   * when no per-company series exists, so the seed is transparent to
   * consumers. `currentValue` is preserved across boots.
   *
   * Ignored (no-op) when the doctype's features don't include `"naming"`.
   */
  defaultSeries?: {
    prefix: string;
    resetEvery?: "never" | "yearly" | "monthly";
    seriesName?: string;
  };

  /**
   * Default workflow seeded for this doctype on company creation (#427
   * follow-up). Module-level `seed*Data()` functions read this field via
   * the registry and call `createWorkflow()` when a company first enables
   * the module — moving the template definition next to the doctype it
   * applies to keeps "what does this doctype look like end-to-end" in one
   * place (ERPNext / Odoo style). Idempotent at the seed call site.
   *
   * Structurally compatible with `Omit<CreateWorkflowInput, "companyId" |
   * "module" | "doctype">` from `modules/workflow/types`. The seed call
   * site supplies `companyId` plus `moduleId` / `id` from this same
   * doctype, so the template only carries the workflow's own fields
   * (name, states, transitions). Types are duplicated here rather than
   * imported from workflow to avoid a core → workflow dependency.
   *
   * Ignored when the doctype's features don't include `"workflow"`.
   */
  workflowTemplate?: DoctypeWorkflowTemplate;

  /**
   * #496 — Canonical document lifecycle contract for ERP documents.
   *
   * This is module-declared source of truth for submit/cancel/return/reopen
   * semantics: which states are immutable, which actions are available, how
   * state maps to docStatus, and whether cancellation is status-only or must
   * create reversal effects. The registry stores it as JSON so generic UI,
   * workflow, audit, and module routes can read one contract instead of
   * hard-coding per-doctype status rules.
   */
  lifecycle?: DocumentLifecycleContract | null;
}

/**
 * Per-state shape used inside `DoctypeDefinition.workflowTemplate.states`.
 * Mirrors the workflow module's `WorkflowStateDetail` minus the runtime
 * fields (`id`, `workflowId`) that only exist after the row is created.
 */
export interface DoctypeWorkflowStateTemplate {
  key: string;
  label: string;
  stateType: "start" | "intermediate" | "success" | "fail";
  color?: string;
  docStatus?: number;
  sortOrder?: number;
  positionX?: number;
  positionY?: number;
}

/**
 * Per-transition shape used inside `DoctypeDefinition.workflowTemplate.transitions`.
 * Lookup keys (`fromStateKey`, `toStateKey`) reference `state.key` rather
 * than ULIDs because the seed runtime mints ULIDs at insert time.
 */
export interface DoctypeWorkflowTransitionTemplate {
  fromStateKey: string;
  toStateKey: string;
  label: string;
  /** Matches the union on `CreateWorkflowInput.transitions[].formType` so the
   *  template can be spread into createWorkflow() without a cast. */
  formType?: "none" | "dynamic" | "custom";
  formSchema?: string;
  customFormKey?: string;
  actionHook?: string;
  conditions?: string;
  sortOrder?: number;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface DoctypeWorkflowTemplate {
  name: string;
  description?: string;
  isDefault?: boolean;
  states: DoctypeWorkflowStateTemplate[];
  transitions: DoctypeWorkflowTransitionTemplate[];
}

/**
 * Caller-facing input shape for `defineDoctype()` — supports either
 * `tableName: "..."` (legacy) OR `table: drizzleTable` (preferred). When
 * `table` is supplied, the column-referencing fields (`numberingField`,
 * `displayField`, `imageField`, `searchFields`, `columnLabels`) are
 * constrained at compile time to the table's snake_case columns.
 *
 * The output stored shape (`DoctypeDefinition` above) keeps `tableName` as
 * a required string so consumers can read it without null-checking — the
 * runtime resolves `table` → `tableName` via `getTableName()`.
 */
export type DoctypeDefinitionInput<TTable extends Table = Table> = Omit<
  DoctypeDefinition,
  | "tableName"
  | "numberingField"
  | "displayField"
  | "imageField"
  | "searchFields"
  | "columnLabels"
> & {
  /** Physical tenant-DB table name. Optional when `table:` is supplied —
   *  derived from the Drizzle table's runtime name. */
  tableName?: string;
  /** Drizzle table object the doctype targets. When supplied, all
   *  column-referencing fields below are constrained to the table's
   *  snake_case columns at compile time. Strongly preferred over
   *  `tableName`-only registration — catches schema drift in the editor
   *  before it reaches a tenant. */
  table?: TTable;
  numberingField?: ColumnDbNames<TTable> | null;
  displayField?: ColumnDbNames<TTable> | null;
  imageField?: ColumnDbNames<TTable> | null;
  searchFields?: readonly ColumnDbNames<TTable>[] | null;
  columnLabels?:
    | (TTable extends Table
        ? { readonly [K in ColumnDbNames<TTable>]?: string }
        : Readonly<Record<string, string>>)
    | null;
};

/** Result of `validateDoctypeDefinition` — discriminated union. */
export type ValidationResult =
  | { valid: true }
  | { valid: false; errors: string[] };

// ----------------------------------------------------------------------------
// Validation helpers — pulled out so they're independently unit-testable.
// ----------------------------------------------------------------------------

const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;
// Reject control + format characters in UI-facing text like `label`.
//   - \p{Cc} — C0/C1 control chars (\n, \t, \x00, etc.)
//   - \p{Cf} — format chars including RTL override U+202E and ZWJ/ZWNJ
// Keeps rendering predictable and blocks homograph/spoofing attacks on
// doctype names (e.g. "invo‮ice" that renders reversed).
const CONTROL_CHAR_RE = /[\p{Cc}\p{Cf}]/u;

// Length bounds — conservative caps that match typical SQLite identifier
// ergonomics and leave room for Thai UI copy.
const MAX_ID = 64;
const MAX_MODULE_ID = 32;
const MAX_TABLE_NAME = 64;
const MAX_LABEL = 255;
const MAX_ICON = 64;

function isSnakeCase(value: string): boolean {
  return SNAKE_CASE_RE.test(value);
}

function isCascadePolicy(value: unknown): value is DoctypeChildCascadePolicy {
  return (
    typeof value === "string" &&
    (DOCTYPE_CHILD_CASCADE_POLICIES as readonly string[]).includes(value)
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function defaultCatalogPolicy(kind: DoctypeKind): ResolvedDoctypeCatalogPolicy {
  const readOnly = kind === "ledger";
  return {
    isReadOnly: readOnly,
    allowGenericCreate: false,
    allowGenericUpdate: false,
    allowGenericDelete: false,
    isImportable: false,
    isExportable: false,
    isReportable: false,
    isSearchable: false,
  };
}

export function resolveDoctypeCatalogPolicy(
  kind: DoctypeKind,
  policy: DoctypeCatalogPolicy | null | undefined,
): ResolvedDoctypeCatalogPolicy {
  const resolved = defaultCatalogPolicy(kind);
  if (!isPlainObject(policy)) return resolved;

  for (const key of CATALOG_POLICY_KEYS) {
    if (typeof policy[key] === "boolean") {
      resolved[key] = policy[key];
    }
  }

  if (kind === "ledger") {
    resolved.isReadOnly = true;
  }
  if (kind === "ledger" || kind === "child_table" || kind === "system") {
    resolved.allowGenericCreate = false;
    resolved.allowGenericUpdate = false;
    resolved.allowGenericDelete = false;
  }
  return resolved;
}

function normalizeCatalogPolicy(
  kind: DoctypeKind,
  policy: DoctypeCatalogPolicy | null | undefined,
): ResolvedDoctypeCatalogPolicy {
  return resolveDoctypeCatalogPolicy(kind, policy);
}

function normalizeCatalogMetadata(
  kind: DoctypeKind,
  catalog: DoctypeCatalogMetadata | null | undefined,
): ResolvedDoctypeCatalogMetadata {
  const rawChildTable = isPlainObject(catalog?.childTable)
    ? catalog.childTable
    : null;
  const childTable =
    rawChildTable &&
    typeof rawChildTable.parentDoctype === "string" &&
    typeof rawChildTable.parentKeyField === "string"
    ? Object.freeze({
        parentDoctype: rawChildTable.parentDoctype,
        parentField:
          typeof rawChildTable.parentField === "string"
            ? rawChildTable.parentField
            : null,
        parentKeyField: rawChildTable.parentKeyField,
        sortField:
          typeof rawChildTable.sortField === "string"
            ? rawChildTable.sortField
            : null,
        cascadePolicy: isCascadePolicy(rawChildTable.cascadePolicy)
          ? rawChildTable.cascadePolicy
          : "cascade",
      })
    : null;
  return Object.freeze({
    childTable,
    policy: Object.freeze(
      normalizeCatalogPolicy(
        kind,
        isPlainObject(catalog?.policy) ? catalog.policy : null,
      ),
    ),
  });
}

/**
 * Convert kebab-case moduleId ("car-rental") → snake_case ("car_rental") for
 * the tableName prefix check. Mirrors `defineTable`'s `normaliseModuleId` in
 * `packages/sdk/src/schema.ts` — MODULES constants in the repo are kebab
 * (used in URLs / workflow data / API routes) while DB table prefixes must
 * be snake. Accepting kebab here keeps `defineDoctype` compatible with the
 * already-declared `MODULES.CAR_RENTAL = "car-rental"` style.
 */
function toDbModuleId(moduleId: string): string {
  return moduleId.replace(/-/g, "_");
}

/**
 * Pure validator — inspects a caller definition and returns a structured
 * result. Does not throw. Used by `defineDoctype()` for fail-fast errors
 * and can be called independently by tooling (migration scripts, admin UI
 * preview, etc.).
 */
export function validateDoctypeDefinition(
  def: DoctypeDefinition,
): ValidationResult {
  const errors: string[] = [];

  // --- id ---
  if (!def.id || typeof def.id !== "string") {
    errors.push("id is required and must be a non-empty string");
  } else if (!isSnakeCase(def.id)) {
    errors.push(
      `id "${def.id}" must be snake_case (start with a lowercase letter, ` +
        `contain only [a-z0-9_])`,
    );
  } else if (def.id.length > MAX_ID) {
    errors.push(`id must be at most ${MAX_ID} chars (got ${def.id.length})`);
  }

  // --- moduleId ---
  // Accepts kebab ("car-rental") or snake ("car_rental"). The SDK's
  // `defineTable` already normalises the same way for physical table
  // prefixes, so any valid MODULES.* constant flows through unchanged.
  if (!def.moduleId || typeof def.moduleId !== "string") {
    errors.push("moduleId is required and must be a non-empty string");
  } else if (!isSnakeCase(toDbModuleId(def.moduleId))) {
    errors.push(
      `moduleId "${def.moduleId}" must be snake_case or kebab-case ` +
        `(start with a lowercase letter, contain only [a-z0-9_-])`,
    );
  } else if (def.moduleId.length > MAX_MODULE_ID) {
    errors.push(
      `moduleId must be at most ${MAX_MODULE_ID} chars (got ${def.moduleId.length})`,
    );
  }

  // --- tableName ---
  // (Uniqueness of a moduleId relative to other modules is NOT checked here;
  // that's enforced by the module-table-prefix rule via lint on the repo as a
  // whole — a doctype-register call has no visibility into other modules.)
  if (!def.tableName || typeof def.tableName !== "string") {
    errors.push("tableName is required and must be a non-empty string");
  } else if (!isSnakeCase(def.tableName)) {
    errors.push(`tableName "${def.tableName}" must be snake_case`);
  } else if (def.tableName.length > MAX_TABLE_NAME) {
    errors.push(
      `tableName must be at most ${MAX_TABLE_NAME} chars (got ${def.tableName.length})`,
    );
  } else if (
    // Only validate prefix if moduleId itself looks sane, to avoid
    // cascading errors for callers with a missing moduleId. Use the
    // snake-normalised moduleId for the prefix so kebab inputs like
    // "car-rental" still resolve against table `car_rental_…`.
    def.moduleId &&
    isSnakeCase(toDbModuleId(def.moduleId)) &&
    !def.tableName.startsWith(`${toDbModuleId(def.moduleId)}_`)
  ) {
    // Mirrors the module-table-prefix rule enforced repo-wide — every
    // DocType table must start with its owner module prefix. Cross-module
    // shared tables are intentionally unsupported under the strict
    // 1 DocType = 1 physical table invariant.
    errors.push(
      `tableName "${def.tableName}" must start with module prefix ` +
        `"${toDbModuleId(def.moduleId)}_" (see rules/module-table-prefix.md)`,
    );
  }

  // --- label ---
  if (!def.label || typeof def.label !== "string") {
    errors.push("label is required and must be a non-empty string");
  } else if (def.label.length > MAX_LABEL) {
    errors.push(
      `label must be at most ${MAX_LABEL} chars (got ${def.label.length})`,
    );
  } else if (CONTROL_CHAR_RE.test(def.label)) {
    // Reject newlines, tabs, RTL-override chars, etc. — keeps UI rendering
    // predictable and blocks homograph-style attacks on doctype names.
    errors.push("label must not contain control characters");
  }

  // --- icon (optional) ---
  if (def.icon !== undefined) {
    if (typeof def.icon !== "string") {
      errors.push("icon, if provided, must be a string");
    } else if (def.icon.length > MAX_ICON) {
      errors.push(
        `icon must be at most ${MAX_ICON} chars (got ${def.icon.length})`,
      );
    }
  }

  // --- features ---
  if (!Array.isArray(def.features)) {
    errors.push("features must be an array");
  } else {
    for (const feature of def.features) {
      if (!isDoctypeFeature(feature)) {
        errors.push(
          `features contains unknown value ${JSON.stringify(feature)}; ` +
            `allowed: ${DOCTYPE_FEATURES.join(", ")}`,
        );
      }
    }
  }

  // --- tier ---
  if (def.tier !== 0 && def.tier !== 1 && def.tier !== 2) {
    errors.push(
      `tier must be 0, 1, or 2 (got ${JSON.stringify(def.tier)})`,
    );
  }

  // --- kind / catalog metadata (#644) ---
  const kind: DoctypeKind = def.kind ?? "normal";
  if (def.kind !== undefined && !isDoctypeKind(def.kind)) {
    errors.push(
      `kind must be one of normal, child_table, submittable, ledger, junction, system, single (got ${JSON.stringify(def.kind)})`,
    );
  }

  const rawCatalog = def.catalog as unknown;
  if (
    rawCatalog !== null &&
    rawCatalog !== undefined &&
    !isPlainObject(rawCatalog)
  ) {
    errors.push("catalog must be an object or null");
  }

  const catalog = isPlainObject(rawCatalog)
    ? (rawCatalog as DoctypeCatalogMetadata)
    : null;
  const rawChildTable = catalog?.childTable as unknown;
  if (
    rawChildTable !== null &&
    rawChildTable !== undefined &&
    !isPlainObject(rawChildTable)
  ) {
    errors.push("catalog.childTable must be an object or null");
  }
  const childTable = isPlainObject(rawChildTable)
    ? (rawChildTable as unknown as DoctypeChildTableRelation)
    : null;
  if (kind === "child_table" && !childTable) {
    errors.push("kind child_table requires catalog.childTable metadata");
  }
  if (kind !== "child_table" && childTable) {
    errors.push("catalog.childTable metadata is only valid when kind is child_table");
  }
  if (childTable) {
    const childFields: Array<[unknown, string, boolean]> = [
      [childTable.parentDoctype, "catalog.childTable.parentDoctype", true],
      [childTable.parentKeyField, "catalog.childTable.parentKeyField", true],
      [childTable.parentField, "catalog.childTable.parentField", false],
      [childTable.sortField, "catalog.childTable.sortField", false],
    ];
    for (const [value, label, required] of childFields) {
      if (value === null || value === undefined || value === "") {
        if (required) errors.push(`${label} is required`);
        continue;
      }
      if (typeof value !== "string" || !isSnakeCase(value) || value.length > 64) {
        errors.push(`${label} must be snake_case and at most 64 chars`);
      }
    }
    if (
      childTable.cascadePolicy !== undefined &&
      !isCascadePolicy(childTable.cascadePolicy)
    ) {
      errors.push(
        `catalog.childTable.cascadePolicy must be cascade, restrict, or orphan (got ${JSON.stringify(childTable.cascadePolicy)})`,
      );
    }
  }

  const rawPolicy = catalog?.policy as unknown;
  const policy = isPlainObject(rawPolicy)
    ? (rawPolicy as DoctypeCatalogPolicy)
    : null;
  if (
    rawPolicy !== null &&
    rawPolicy !== undefined &&
    !isPlainObject(rawPolicy)
  ) {
    errors.push("catalog.policy must be an object when provided");
  } else if (policy) {
    for (const key of Object.keys(policy)) {
      if (!CATALOG_POLICY_KEYS.includes(key as keyof DoctypeCatalogPolicy)) {
        errors.push(`catalog.policy contains unknown key "${key}"`);
      }
    }
    for (const key of CATALOG_POLICY_KEYS) {
      if (policy[key] !== undefined && typeof policy[key] !== "boolean") {
        errors.push(`catalog.policy.${key} must be a boolean`);
      }
    }
    const mutatingGenericAllowed =
      policy.allowGenericCreate === true ||
      policy.allowGenericUpdate === true ||
      policy.allowGenericDelete === true;
    if (mutatingGenericAllowed && (kind === "ledger" || kind === "child_table" || kind === "system")) {
      errors.push(`kind ${kind} cannot enable generic create/update/delete`);
    }
    if (kind === "ledger" && policy.isReadOnly === false) {
      errors.push("kind ledger must remain read-only");
    }
    if (
      policy.isReadOnly === true &&
      (policy.allowGenericCreate === true ||
        policy.allowGenericUpdate === true ||
        policy.allowGenericDelete === true)
    ) {
      errors.push("catalog.policy cannot be read-only and allow generic writes");
    }
  }

  // #365 — metadata fields (numberingField, displayField, imageField,
  // searchFields). All optional + module-declared; they point at columns
  // on the doctype's physical table. We validate shape (snake_case, size
  // bounds) but NOT that the column actually exists on the table — runtime
  // does that when generateNextNumber() / list queries execute.
  const MAX_FIELD_NAME = 64;
  const checkFieldName = (value: unknown, label: string): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") {
      return `${label}, if provided, must be a string or null`;
    }
    if (!isSnakeCase(value)) {
      return `${label} "${value}" must be snake_case`;
    }
    if (value.length > MAX_FIELD_NAME) {
      return `${label} must be at most ${MAX_FIELD_NAME} chars`;
    }
    return null;
  };

  const numberingFieldErr = checkFieldName(def.numberingField, "numberingField");
  if (numberingFieldErr) errors.push(numberingFieldErr);

  const displayFieldErr = checkFieldName(def.displayField, "displayField");
  if (displayFieldErr) errors.push(displayFieldErr);

  const imageFieldErr = checkFieldName(def.imageField, "imageField");
  if (imageFieldErr) errors.push(imageFieldErr);

  // --- searchFields ---
  if (def.searchFields !== null && def.searchFields !== undefined) {
    if (!Array.isArray(def.searchFields)) {
      errors.push("searchFields, if provided, must be an array of strings");
    } else {
      for (const entry of def.searchFields) {
        const err = checkFieldName(entry, "searchFields entry");
        if (err) errors.push(err);
      }
    }
  }

  // --- cross-field: "naming" feature ⇔ numberingField coherence ---
  // Without this check, a module could declare features=["naming"] but
  // leave numberingField null → generateNextNumber has no column to
  // write to; or declare numberingField="x" but omit "naming" → seed
  // logic skips, admin UI says "not a naming doctype" but the table HAS
  // a dedicated column. Both end in silent confusion at runtime.
  const hasNamingFeature =
    Array.isArray(def.features) && def.features.includes("naming");
  const hasNumberingField =
    typeof def.numberingField === "string" && def.numberingField.length > 0;
  if (hasNamingFeature && !hasNumberingField) {
    errors.push(
      'features includes "naming" but numberingField is null — declare the column that should receive the generated number (e.g. numberingField: "invoice_no")',
    );
  }
  if (hasNumberingField && !hasNamingFeature) {
    errors.push(
      `numberingField "${def.numberingField}" is set but features does not include "naming" — add "naming" to features, or remove numberingField to use ULID`,
    );
  }

  // --- lifecycle ---
  errors.push(...validateLifecycleContract(def.lifecycle));

  // --- defaultSeries ---
  // Optional seed for core_numbering_series. Only meaningful when the
  // doctype opts into the "naming" feature — but we validate shape
  // regardless so typos surface early.
  if (def.defaultSeries !== null && def.defaultSeries !== undefined) {
    const ds = def.defaultSeries;
    if (typeof ds !== "object" || Array.isArray(ds)) {
      errors.push("defaultSeries, if provided, must be an object");
    } else {
      if (typeof ds.prefix !== "string" || ds.prefix.length === 0) {
        errors.push("defaultSeries.prefix must be a non-empty string");
      } else if (ds.prefix.length > 128) {
        errors.push("defaultSeries.prefix must be at most 128 chars");
      } else if (CONTROL_CHAR_RE.test(ds.prefix)) {
        errors.push("defaultSeries.prefix must not contain control characters");
      }
      if (
        ds.resetEvery !== undefined &&
        ds.resetEvery !== "never" &&
        ds.resetEvery !== "yearly" &&
        ds.resetEvery !== "monthly"
      ) {
        errors.push(
          `defaultSeries.resetEvery must be "never" | "yearly" | "monthly" (got ${JSON.stringify(ds.resetEvery)})`,
        );
      }
      if (ds.seriesName !== undefined) {
        if (typeof ds.seriesName !== "string") {
          errors.push("defaultSeries.seriesName, if provided, must be a string");
        } else if (ds.seriesName.length > 128) {
          errors.push("defaultSeries.seriesName must be at most 128 chars");
        }
      }
    }
  }

  // --- columnLabels ---
  // Shape: Record<snake_case, non-empty Thai/English string>. Size-cap each
  // label to keep UI rendering predictable and cap the object size so a
  // runaway payload can't bloat the registry row. No control-char check on
  // values — Thai labels sometimes include specific Unicode marks that
  // \p{Cf} would flag incorrectly (see discussion in #365 PR).
  const MAX_LABEL_VALUE = 128;
  const MAX_LABELS_ENTRIES = 200;
  if (def.columnLabels !== null && def.columnLabels !== undefined) {
    if (typeof def.columnLabels !== "object" || Array.isArray(def.columnLabels)) {
      errors.push("columnLabels, if provided, must be an object of { column: label } pairs");
    } else {
      const entries = Object.entries(def.columnLabels);
      if (entries.length > MAX_LABELS_ENTRIES) {
        errors.push(
          `columnLabels must have at most ${MAX_LABELS_ENTRIES} entries (got ${entries.length})`,
        );
      }
      for (const [key, val] of entries) {
        const keyErr = checkFieldName(key, `columnLabels key "${key}"`);
        if (keyErr) errors.push(keyErr);
        if (typeof val !== "string") {
          errors.push(`columnLabels["${key}"] must be a string`);
        } else if (val.length === 0) {
          errors.push(`columnLabels["${key}"] must not be empty`);
        } else if (val.length > MAX_LABEL_VALUE) {
          errors.push(
            `columnLabels["${key}"] must be at most ${MAX_LABEL_VALUE} chars (got ${val.length})`,
          );
        }
      }
    }
  }

  return errors.length === 0 ? { valid: true } : { valid: false, errors };
}

// ----------------------------------------------------------------------------
// defineDoctype — pure helper, safe inside a module's `index.ts` manifest.
// ----------------------------------------------------------------------------

/**
 * Validate and freeze a doctype definition.
 *
 * Call this from a module's `index.ts` or anywhere at module load time.
 * Throws with a readable error if the definition is invalid.
 *
 * Does NOT touch the database. The actual registry upsert happens in
 * `registerDoctype()` which is invoked by the platform boot path.
 *
 * @example
 * ```ts
 * // modules/sales/index.ts
 * import { defineDoctype, defineModule } from '@kubdee/sdk';
 *
 * const quotationDoctype = defineDoctype({
 *   id: 'quotation',
 *   moduleId: 'sales',
 *   tableName: 'sales_quotations',
 *   label: 'ใบเสนอราคา',
 *   icon: 'FileText',
 *   features: ['naming', 'custom_fields', 'workflow', 'print'],
 *   tier: 2,
 * });
 * ```
 */
export function defineDoctype<TTable extends Table = Table>(
  def: DoctypeDefinitionInput<TTable>,
): Readonly<DoctypeDefinition> {
  // Derive `tableName` from the supplied Drizzle `table` when present —
  // single source of truth, prevents the two from drifting. If both are
  // present we trust `table` and ignore the explicit string.
  const derivedTableName = def.table ? getTableName(def.table) : undefined;
  const tableName = derivedTableName ?? def.tableName ?? "";
  const kind = def.kind ?? "normal";
  const catalog = normalizeCatalogMetadata(kind, def.catalog);
  const validationCatalog =
    (def.catalog as DoctypeCatalogMetadata | null | undefined) ?? null;

  // Project to the stored shape for the validator so existing checks
  // (snake_case, prefix rule) keep working unchanged. The `table` arg is
  // not stored — its only purpose was the compile-time generic on input.
  const flat: DoctypeDefinition = {
    id: def.id,
    moduleId: def.moduleId,
    tableName,
    label: def.label,
    icon: def.icon,
    features: def.features,
    tier: def.tier,
    kind,
    catalog: validationCatalog,
    numberingField: (def.numberingField as string | null | undefined) ?? null,
    displayField: (def.displayField as string | null | undefined) ?? null,
    imageField: (def.imageField as string | null | undefined) ?? null,
    searchFields: (def.searchFields as readonly string[] | null | undefined) ?? null,
    columnLabels: (def.columnLabels as Readonly<Record<string, string>> | null | undefined) ?? null,
    defaultSeries: def.defaultSeries,
    lifecycle: def.lifecycle ? normalizeLifecycleContract(def.lifecycle) : null,
  };

  const result = validateDoctypeDefinition(flat);
  if (!result.valid) {
    throw new Error(
      `[defineDoctype] Invalid definition for "${def.id ?? "<unknown>"}":\n` +
        result.errors.map((e) => `  - ${e}`).join("\n"),
    );
  }

  // Defense-in-depth: when `table` is supplied, runtime-check that every
  // referenced column actually exists on it. The TS generics catch this
  // at compile time, but a stale type definition or a `as any` upstream
  // could still let an invalid name through — surface here loud and
  // early instead of waiting for a tenant query to fail.
  if (def.table) {
    const tableCols = getColumnDbNames(def.table);
    const referenced: { field: string; value: string }[] = [];
    if (def.numberingField) referenced.push({ field: "numberingField", value: def.numberingField as string });
    if (def.displayField) referenced.push({ field: "displayField", value: def.displayField as string });
    if (def.imageField) referenced.push({ field: "imageField", value: def.imageField as string });
    for (const f of (def.searchFields as readonly string[] | null | undefined) ?? []) {
      referenced.push({ field: "searchFields", value: f });
    }
    for (const k of Object.keys(def.columnLabels ?? {})) {
      referenced.push({ field: "columnLabels", value: k });
    }
    if (def.catalog?.childTable?.parentKeyField) {
      referenced.push({
        field: "catalog.childTable.parentKeyField",
        value: def.catalog.childTable.parentKeyField,
      });
    }
    if (def.catalog?.childTable?.sortField) {
      referenced.push({
        field: "catalog.childTable.sortField",
        value: def.catalog.childTable.sortField,
      });
    }
    const missing = referenced.filter((r) => !tableCols.has(r.value));
    if (missing.length > 0) {
      throw new Error(
        `[defineDoctype] Definition for "${def.id}" references columns not on table "${tableName}":\n` +
          missing
            .map((m) => `  - ${m.field}: "${m.value}"`)
            .join("\n") +
          `\nKnown columns: ${[...tableCols].sort().join(", ")}`,
      );
    }
  }

  // Normalize features: deep-freeze so downstream callers can't mutate.
  // We intentionally keep the caller's feature order to make diffs stable.
  // Normalize `icon` to `undefined` (omitted vs explicit undefined are the
  // same shape — avoids surprising Object.keys() diffs between calls).
  // #365 metadata fields normalize to null when the caller omits them (so
  // null/undefined aren't distinct in stored output).
  const frozen: DoctypeDefinition = {
    id: def.id,
    moduleId: def.moduleId,
    tableName,
    label: def.label,
    icon: def.icon ?? undefined,
    features: Object.freeze([...def.features]) as DoctypeFeature[],
    tier: def.tier,
    kind,
    catalog,
    numberingField: (def.numberingField as string | null | undefined) ?? null,
    displayField: (def.displayField as string | null | undefined) ?? null,
    imageField: (def.imageField as string | null | undefined) ?? null,
    searchFields: def.searchFields
      ? (Object.freeze([...(def.searchFields as readonly string[])]) as readonly string[])
      : null,
    columnLabels: def.columnLabels
      ? Object.freeze({ ...(def.columnLabels as Record<string, string>) })
      : null,
    defaultSeries: def.defaultSeries
      ? Object.freeze({
          prefix: def.defaultSeries.prefix,
          resetEvery: def.defaultSeries.resetEvery ?? "yearly",
          seriesName: def.defaultSeries.seriesName,
        })
      : undefined,
    // workflowTemplate is plain config data — freeze it shallowly so seed
    // call sites can spread it but can't mutate the registered template.
    // states/transitions arrays inside aren't frozen recursively because
    // the seed call site doesn't mutate them either; deep freeze would be
    // pure overhead.
    workflowTemplate: def.workflowTemplate
      ? Object.freeze({ ...def.workflowTemplate })
      : undefined,
    lifecycle: def.lifecycle ? normalizeLifecycleContract(def.lifecycle) : null,
  };
  return Object.freeze(frozen);
}

/**
 * Pull the runtime snake_case DB column names off a Drizzle table object.
 * Used by `defineDoctype()` to runtime-validate column references on the
 * supplied `table:` arg as a backstop for the compile-time generic.
 */
function getColumnDbNames(table: Table): Set<string> {
  // `getTableColumns(table)` is Drizzle's public helper that returns the
  // JS-side property → Column map. Each `Column` exposes `.name` as the
  // snake_case DB name (set via `text("snake_name")` in the schema).
  const cols = getTableColumns(table);
  return new Set(Object.values(cols).map((c) => (c as { name: string }).name));
}

// ----------------------------------------------------------------------------
// registerDoctype — runtime upsert (platform-default rows, company_id=NULL).
// ----------------------------------------------------------------------------

/**
 * Upsert the platform-default registry row for this doctype.
 *
 * Idempotent: safe to call on every boot. If the row already exists, metadata
 * fields (`label`, `icon`, `features`, `tableName`, `moduleId`, `tier`,
 * `updatedAt`) are refreshed so module devs can tweak them in code and
 * reboot. The row's `createdAt` is preserved.
 *
 * Always writes `company_id = NULL`. Per-company overrides come from an
 * explicit user action in the admin UI (not this path).
 *
 * @throws if the definition is invalid (fails fast, same logic as
 *         `defineDoctype`) or if the DB write fails.
 */
export async function registerDoctype(
  db: DrizzleD1Database,
  def: DoctypeDefinition,
): Promise<void> {
  const result = validateDoctypeDefinition(def);
  if (!result.valid) {
    throw new Error(
      `[registerDoctype] Invalid definition for "${def.id ?? "<unknown>"}":\n` +
        result.errors.map((e) => `  - ${e}`).join("\n"),
    );
  }

  const now = nowIso();
  const featuresJson = JSON.stringify(def.features);
  // #365 — searchFields persisted as JSON text (nullable). Array→JSON at the
  // boundary so SQLite stores a single scalar; reads re-parse via JSON.parse.
  const searchFieldsJson = def.searchFields
    ? JSON.stringify([...def.searchFields])
    : null;
  // #365 follow-up — columnLabels persisted as JSON object text (nullable).
  const columnLabelsJson = def.columnLabels
    ? JSON.stringify({ ...def.columnLabels })
    : null;
  const lifecycleJson = def.lifecycle
    ? JSON.stringify(def.lifecycle)
    : null;
  const kind = def.kind ?? "normal";
  const catalogMetadataJson = JSON.stringify(
    normalizeCatalogMetadata(kind, def.catalog),
  );

  // Target the partial UNIQUE `uq_doctype_registry_default (id) WHERE
  // company_id IS NULL`. SQLite's plain UNIQUE treats NULL as distinct, so
  // targeting just `id` without the partial predicate would never fire for
  // default rows — `targetWhere` is mandatory here.
  await db
    .insert(doctypeRegistry)
    .values({
      id: def.id,
      moduleId: def.moduleId,
      companyId: null,
      tableName: def.tableName,
      label: def.label,
      icon: def.icon ?? null,
      features: featuresJson,
      tier: def.tier,
      kind,
      catalogMetadata: catalogMetadataJson,
      numberingField: def.numberingField ?? null,
      displayField: def.displayField ?? null,
      imageField: def.imageField ?? null,
      searchFields: searchFieldsJson,
      columnLabels: columnLabelsJson,
      lifecycle: lifecycleJson,
      createdAt: now,
      updatedAt: now,
    })
    .onConflictDoUpdate({
      target: doctypeRegistry.id,
      targetWhere: sql`${doctypeRegistry.companyId} IS NULL`,
      set: {
        moduleId: def.moduleId,
        tableName: def.tableName,
        label: def.label,
        icon: def.icon ?? null,
        features: featuresJson,
        tier: def.tier,
        kind,
        catalogMetadata: catalogMetadataJson,
        numberingField: def.numberingField ?? null,
        displayField: def.displayField ?? null,
        imageField: def.imageField ?? null,
        searchFields: searchFieldsJson,
        columnLabels: columnLabelsJson,
        lifecycle: lifecycleJson,
        // createdAt is intentionally preserved on update — only updatedAt bumps.
        updatedAt: now,
      },
    });

  // Seed the platform-default numbering series so the doctype is usable
  // on first request instead of throwing NOT_FOUND at generateNextNumber.
  // No-op when the doctype doesn't have the "naming" feature or didn't
  // declare a defaultSeries.
  if (def.features.includes("naming") && def.defaultSeries) {
    await seedDefaultSeries(db, {
      module: def.moduleId,
      doctype: def.id,
      defaultSeries: def.defaultSeries,
    });
  }
}

/**
 * Idempotent upsert of the platform-default (company_id = NULL) numbering
 * series for this doctype. Preserves `currentValue` and `createdAt` across
 * reboots so an existing running counter is never reset by a code change.
 *
 * Pure helper — exported for module-boot code paths that need to seed
 * independently of the full registerDoctype flow (e.g. tests).
 */
export async function seedDefaultSeries(
  db: DrizzleD1Database,
  params: {
    module: string;
    doctype: string;
    defaultSeries: {
      prefix: string;
      resetEvery?: "never" | "yearly" | "monthly";
      seriesName?: string;
    };
  },
): Promise<void> {
  const { module, doctype, defaultSeries: ds } = params;
  const resetEvery = ds.resetEvery ?? "yearly";
  const now = nowIso();

  // Check whether a platform-default row already exists. We match on
  // (module, doctype, company_id IS NULL) — this tuple is what
  // findSeries() falls back to, so it's the right idempotency key.
  const existing = await db
    .select({ id: numberingSeries.id })
    .from(numberingSeries)
    .where(
      and(
        eq(numberingSeries.module, module),
        eq(numberingSeries.doctype, doctype),
        isNull(numberingSeries.companyId),
      ),
    )
    .get();

  if (existing) {
    // Update pattern/reset to follow module-declared source of truth,
    // but NEVER touch currentValue/lastResetYear/lastResetMonth — those
    // belong to the running counter.
    await db
      .update(numberingSeries)
      .set({
        prefix: ds.prefix,
        resetEvery,
        seriesName: ds.seriesName ?? null,
        updatedAt: now,
      })
      .where(eq(numberingSeries.id, existing.id));
  } else {
    // Fresh insert. isDefault=1 so the partial UNIQUE doesn't fire on
    // concurrent boots — ON CONFLICT would need a targetWhere here; a
    // pre-check is simpler.
    await db.insert(numberingSeries).values({
      id: generateUlid(),
      module,
      doctype,
      companyId: null,
      prefix: ds.prefix,
      resetEvery,
      seriesName: ds.seriesName ?? null,
      currentValue: 0,
      autoGenerate: 1,
      isActive: 1,
      isDefault: 1,
      createdAt: now,
      updatedAt: now,
    });
  }
}

// ============================================================================
// updateDoctypeMetadata — admin-side per-company override of #365 fields.
//
// The registry already supports per-company overrides via `(id, company_id)`.
// Module-declared rows have `company_id = NULL` (boot-loaded by
// registerDoctype). When the admin tweaks one of the #365 metadata fields
// from the Doctype Admin UI, we upsert a SECOND row with `company_id` set
// to the caller's tenant company. `listDoctypes(db, companyId)` already
// prefers the company-scoped row over the default — no read change needed.
//
// Only the four #365 fields (numberingField, displayField, imageField,
// searchFields) are admin-mutable. Identity (id, moduleId, tableName,
// label, icon, features, tier) stays module-declared — flipping those
// per-company would cause schema mismatches the admin can't reason about.
// ============================================================================

export interface UpdateDoctypeMetadataPatch {
  /** Pass `undefined` to leave the field untouched, `null` to clear, or
   *  a string to set a new column reference. */
  numberingField?: string | null;
  displayField?: string | null;
  imageField?: string | null;
  searchFields?: readonly string[] | null;
  columnLabels?: Readonly<Record<string, string>> | null;
}

/**
 * Apply an admin override of the #365 metadata fields for one doctype, scoped
 * to the caller's company. Falls back to the platform default for any field
 * the patch doesn't touch.
 *
 * Validation:
 *   - `companyId` must be a non-empty string (caller's tenant company).
 *   - The doctype must exist in the registry as a platform default — we copy
 *     identity from the default row so the override stays in sync with
 *     `(moduleId, tableName, label, ...)` even if the admin only changes
 *     `displayField`.
 *   - Each non-null column reference must be snake_case (same regex as
 *     defineDoctype's per-field validator).
 *
 * Returns the resolved row (override merged onto default) so callers can
 * update their cache without a refetch round-trip.
 */
export async function updateDoctypeMetadata(
  db: DrizzleD1Database,
  params: {
    doctypeId: string;
    companyId: string;
    patch: UpdateDoctypeMetadataPatch;
  },
): Promise<ResolvedDoctype> {
  if (!params.companyId || typeof params.companyId !== "string") {
    throw new Error(
      "[updateDoctypeMetadata] companyId is required (admin overrides are per-company)",
    );
  }

  // Shape-check each metadata column reference against the same regex that
  // defineDoctype uses, before we hit the DB. Keeps the error surface
  // identical between module-declared and admin-edited paths.
  const SNAKE = /^[a-z][a-z0-9_]*$/;
  const checkCol = (val: unknown, label: string) => {
    if (val === undefined || val === null) return;
    if (typeof val !== "string" || !SNAKE.test(val) || val.length > 64) {
      throw new Error(`[updateDoctypeMetadata] ${label} must be snake_case ≤ 64 chars`);
    }
  };
  checkCol(params.patch.numberingField, "numberingField");
  checkCol(params.patch.displayField, "displayField");
  checkCol(params.patch.imageField, "imageField");
  if (
    params.patch.searchFields !== undefined &&
    params.patch.searchFields !== null
  ) {
    if (!Array.isArray(params.patch.searchFields)) {
      throw new Error(
        "[updateDoctypeMetadata] searchFields must be an array of column names or null",
      );
    }
    for (const f of params.patch.searchFields) {
      checkCol(f, "searchFields entry");
    }
  }

  // #365 follow-up — columnLabels patch shape: Record<snake_case, string>.
  if (
    params.patch.columnLabels !== undefined &&
    params.patch.columnLabels !== null
  ) {
    if (
      typeof params.patch.columnLabels !== "object" ||
      Array.isArray(params.patch.columnLabels)
    ) {
      throw new Error(
        "[updateDoctypeMetadata] columnLabels must be an object of { column: label } or null",
      );
    }
    for (const [k, v] of Object.entries(params.patch.columnLabels)) {
      checkCol(k, `columnLabels key "${k}"`);
      if (typeof v !== "string" || v.length === 0 || v.length > 128) {
        throw new Error(
          `[updateDoctypeMetadata] columnLabels["${k}"] must be a non-empty string ≤ 128 chars`,
        );
      }
    }
  }

  // Pull the platform default — identity columns + any unmodified metadata
  // come from here. If no default exists, the doctype isn't registered;
  // refuse rather than create an orphan override.
  const defaultRow = await db
    .select()
    .from(doctypeRegistry)
    .where(
      and(
        eq(doctypeRegistry.id, params.doctypeId),
        isNull(doctypeRegistry.companyId),
      ),
    )
    .get();
  if (!defaultRow) {
    throw new Error(
      `[updateDoctypeMetadata] doctype "${params.doctypeId}" not registered (no platform default row to override)`,
    );
  }

  // Existing override (if any) — used as the baseline so a patch only needs
  // to send the fields that change. Falls back to the default when none.
  const existingOverride = await db
    .select()
    .from(doctypeRegistry)
    .where(
      and(
        eq(doctypeRegistry.id, params.doctypeId),
        eq(doctypeRegistry.companyId, params.companyId),
      ),
    )
    .get();

  // Merge: explicit `undefined` in the patch means "keep current"; explicit
  // `null` means "clear this metadata field for this company".
  const resolveCol = (
    patchVal: string | null | undefined,
    overrideVal: string | null,
  ): string | null => (patchVal === undefined ? overrideVal : patchVal);

  const numberingField = resolveCol(
    params.patch.numberingField,
    existingOverride?.numberingField ?? defaultRow.numberingField,
  );
  const displayField = resolveCol(
    params.patch.displayField,
    existingOverride?.displayField ?? defaultRow.displayField,
  );
  const imageField = resolveCol(
    params.patch.imageField,
    existingOverride?.imageField ?? defaultRow.imageField,
  );

  // searchFields needs its own merge — array vs null
  const searchFieldsResolved: string[] | null =
    params.patch.searchFields === undefined
      ? existingOverride?.searchFields
        ? (JSON.parse(existingOverride.searchFields) as string[])
        : defaultRow.searchFields
          ? (JSON.parse(defaultRow.searchFields) as string[])
          : null
      : params.patch.searchFields === null
        ? null
        : [...params.patch.searchFields];

  // #365 follow-up — columnLabels merge. Same three-state pattern as above
  // (undefined = keep, null = clear, object = replace wholesale).
  const columnLabelsResolved: Record<string, string> | null =
    params.patch.columnLabels === undefined
      ? existingOverride?.columnLabels
        ? (JSON.parse(existingOverride.columnLabels) as Record<string, string>)
        : defaultRow.columnLabels
          ? (JSON.parse(defaultRow.columnLabels) as Record<string, string>)
          : null
      : params.patch.columnLabels === null
        ? null
        : { ...params.patch.columnLabels };

  const now = nowIso();
  const searchFieldsJson = searchFieldsResolved
    ? JSON.stringify(searchFieldsResolved)
    : null;
  const columnLabelsJson = columnLabelsResolved
    ? JSON.stringify(columnLabelsResolved)
    : null;

  // Upsert — partial UNIQUE on (id) WHERE company_id IS NULL covers default
  // rows; the regular UNIQUE (id, company_id) covers per-company overrides.
  await db
    .insert(doctypeRegistry)
    .values({
      id: defaultRow.id,
      moduleId: defaultRow.moduleId,
      companyId: params.companyId,
      tableName: defaultRow.tableName,
      label: defaultRow.label,
      icon: defaultRow.icon,
      features: defaultRow.features,
      tier: defaultRow.tier,
      kind: defaultRow.kind,
      catalogMetadata: defaultRow.catalogMetadata,
      numberingField,
      displayField,
      imageField,
      searchFields: searchFieldsJson,
      columnLabels: columnLabelsJson,
      lifecycle: defaultRow.lifecycle,
      createdAt: existingOverride?.createdAt ?? now,
      updatedAt: now,
    })
    .onConflictDoUpdate({
      target: [doctypeRegistry.id, doctypeRegistry.companyId],
      set: {
        moduleId: defaultRow.moduleId,
        tableName: defaultRow.tableName,
        label: defaultRow.label,
        icon: defaultRow.icon,
        features: defaultRow.features,
        tier: defaultRow.tier,
        numberingField,
        displayField,
        imageField,
        searchFields: searchFieldsJson,
        columnLabels: columnLabelsJson,
        lifecycle: defaultRow.lifecycle,
        kind: defaultRow.kind,
        catalogMetadata: defaultRow.catalogMetadata,
        updatedAt: now,
      },
    });

  // Re-read the override row so we return the canonical projection.
  const updated = await db
    .select()
    .from(doctypeRegistry)
    .where(
      and(
        eq(doctypeRegistry.id, params.doctypeId),
        eq(doctypeRegistry.companyId, params.companyId),
      ),
    )
    .get();
  if (!updated) {
    throw new Error("[updateDoctypeMetadata] upsert succeeded but readback returned no row");
  }
  return rowToResolved(updated);
}

// ============================================================================
// getDoctypeColumns — runtime introspection of a doctype's table columns.
//
// The Doctype Admin UI uses this to populate dropdowns when the admin edits
// numberingField / displayField / imageField / searchFields — only columns
// that actually exist on the doctype's table should be selectable.
//
// Implementation: PRAGMA table_info(<tableName>). The table name is server-
// resolved from the registry (caller supplies the doctypeId, we look up
// `tableName` ourselves), so there's no SQL-injection surface from user
// input. PRAGMA does NOT support parameter binding for the table name —
// that's why we resolve the name first and template it via sql.raw.
// ============================================================================

export interface DoctypeColumnInfo {
  name: string;
  type: string;
  notnull: boolean;
  pk: boolean;
  defaultValue: string | null;
}

export async function getDoctypeColumns(
  db: DrizzleD1Database,
  doctypeId: string,
): Promise<DoctypeColumnInfo[]> {
  const def = await db
    .select({ tableName: doctypeRegistry.tableName })
    .from(doctypeRegistry)
    .where(
      and(eq(doctypeRegistry.id, doctypeId), isNull(doctypeRegistry.companyId)),
    )
    .get();
  if (!def) {
    throw new Error(
      `[getDoctypeColumns] doctype "${doctypeId}" not in registry`,
    );
  }
  // Defense-in-depth: only allow snake_case table names through to the raw
  // SQL. Registry CHECKs already enforce shape, but be paranoid here too.
  if (!/^[a-z][a-z0-9_]*$/.test(def.tableName)) {
    throw new Error(
      `[getDoctypeColumns] refusing to introspect malformed tableName "${def.tableName}"`,
    );
  }

  // Drizzle's d1 driver returns PRAGMA results as a row array. We use
  // sql.raw because PRAGMA can't bind table names; the regex check above
  // is the safety gate.
  const pragma = sql.raw(`PRAGMA table_info("${def.tableName}")`);
  const runner = db as unknown as {
    all?: (query: typeof pragma) => Promise<unknown>;
    run: (query: typeof pragma) => Promise<unknown>;
  };
  const result = typeof runner.all === "function"
    ? await runner.all(pragma)
    : await runner.run(pragma);
  const rows = (Array.isArray(result)
    ? result
    : ((result as { results?: unknown[]; rows?: unknown[] }).results
      ?? (result as { rows?: unknown[] }).rows
      ?? [])) as ReadonlyArray<{
    name: string;
    type: string;
    notnull: number;
    dflt_value: string | null;
    pk: number;
  }>;
  return rows.map((row) => ({
    name: row.name,
    type: row.type,
    notnull: row.notnull === 1,
    pk: row.pk === 1,
    defaultValue: row.dflt_value ?? null,
  }));
}

/**
 * Batch helper — register multiple doctypes sequentially.
 *
 * Not wrapped in a transaction deliberately: D1's remote execution model
 * treats each statement as a separate call anyway, and failing one doctype
 * should not abort the rest of the batch (a boot-time partial registration
 * is still better than none). Errors are collected and rethrown at the end.
 */
export async function registerDoctypes(
  db: DrizzleD1Database,
  defs: DoctypeDefinition[],
): Promise<void> {
  const errors: Array<{ id: string; error: Error }> = [];
  for (const def of defs) {
    try {
      await registerDoctype(db, def);
    } catch (err) {
      errors.push({
        id: def.id ?? "<unknown>",
        error: err instanceof Error ? err : new Error(String(err)),
      });
    }
  }
  if (errors.length > 0) {
    const summary = errors
      .map(({ id, error }) => `  - ${id}: ${error.message}`)
      .join("\n");
    throw new Error(
      `[registerDoctypes] ${errors.length} doctype(s) failed to register:\n${summary}`,
    );
  }
}

// ----------------------------------------------------------------------------
// Read helper — exported for the Doctype Admin UI / test sugar.
// ----------------------------------------------------------------------------

/**
 * Fetch the platform-default registry row for a doctype id.
 * Returns undefined if not registered yet.
 */
export async function getDoctypeDefault(
  db: DrizzleD1Database,
  id: string,
): Promise<ResolvedDoctype | undefined> {
  const rows = await db
    .select()
    .from(doctypeRegistry)
    .where(
      and(eq(doctypeRegistry.id, id), isNull(doctypeRegistry.companyId)),
    )
    .limit(1);

  const row = rows[0];
  if (!row) return undefined;

  return rowToResolved(row);
}

/**
 * Shared row → ResolvedDoctype projection. Handles:
 *   - features: JSON parse + filter through isDoctypeFeature
 *   - tier: narrow to 0 | 1 | 2 (with defensive fallback)
 *   - searchFields: JSON parse (string[] or null)
 *   - numberingField / displayField / imageField: pass-through (text | null)
 */
function rowToResolved(
  row: typeof doctypeRegistry.$inferSelect,
): ResolvedDoctype {
  let features: DoctypeFeature[] = [];
  try {
    const parsed: unknown = JSON.parse(row.features);
    if (Array.isArray(parsed)) {
      features = parsed.filter(isDoctypeFeature);
    }
  } catch {
    // Malformed features — treat as empty. Schema CHECK normally blocks this.
  }

  let searchFields: string[] | null = null;
  if (row.searchFields !== null) {
    try {
      const parsed: unknown = JSON.parse(row.searchFields);
      if (Array.isArray(parsed)) {
        searchFields = parsed.filter(
          (v): v is string => typeof v === "string",
        );
      }
    } catch {
      // Malformed — fall back to null. Schema CHECK guards this, but stay safe.
    }
  }

  // #365 follow-up — columnLabels: JSON object { column: "ชื่อไทย" } | null.
  // Filter non-string values defensively in case an older client wrote
  // malformed data before the CHECK was in place (none landed yet, but be
  // symmetric with searchFields' defensive parse above).
  let columnLabels: Record<string, string> | null = null;
  if (row.columnLabels !== null) {
    try {
      const parsed: unknown = JSON.parse(row.columnLabels);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const clean: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof v === "string" && v.length > 0) clean[k] = v;
        }
        columnLabels = Object.keys(clean).length > 0 ? clean : null;
      }
    } catch {
      // Malformed — fall back to null.
    }
  }

  let lifecycle: DocumentLifecycleContract | null = null;
  if (row.lifecycle !== null) {
    try {
      lifecycle = parseLifecycleContract(JSON.parse(row.lifecycle));
    } catch {
      lifecycle = null;
    }
  }

  const tier: 0 | 1 | 2 =
    row.tier === 0 || row.tier === 1 || row.tier === 2 ? row.tier : 2;
  const kind: DoctypeKind = isDoctypeKind(row.kind) ? row.kind : "normal";
  let catalog: ResolvedDoctypeCatalogMetadata = normalizeCatalogMetadata(kind, null);
  if (row.catalogMetadata !== null) {
    try {
      const parsed: unknown = JSON.parse(row.catalogMetadata);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        catalog = normalizeCatalogMetadata(kind, parsed as DoctypeCatalogMetadata);
      }
    } catch {
      catalog = normalizeCatalogMetadata(kind, null);
    }
  }

  return {
    id: row.id,
    moduleId: row.moduleId,
    tableName: row.tableName,
    label: row.label,
    icon: row.icon,
    features,
    tier,
    kind,
    catalog,
    numberingField: row.numberingField,
    displayField: row.displayField,
    imageField: row.imageField,
    searchFields,
    columnLabels,
    lifecycle,
    // Numbering enrichment is a list-only concern — populated in `listDoctypes`
    // after this projection. Single-row paths (`getDoctypeDefault`) keep these
    // fields at null; callers that need them go through the list query or hit
    // `numbering.service.ts` directly.
    defaultSeriesPrefix: null,
    defaultSeriesLastIssued: null,
  };
}

// ----------------------------------------------------------------------------
// listDoctypes — company-aware list with ERPNext-style override resolution.
//
// Doctype Admin Epic (#347) issue A3 (#351). The Admin list page + any
// doctype-agnostic consumer reads from this single entrypoint; per-doctype
// hardcoding anywhere in the UI is a red flag.
// ----------------------------------------------------------------------------

const NUMBERING_SERIES_DOCTYPE_BATCH_SIZE = 50;

function chunkArray<T>(values: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

/** Resolved doctype row — what the UI and consumers see after override
 *  resolution. Shape is shared by `getDoctypeDefault` and `listDoctypes`.
 *
 *  The four #365 metadata fields (numberingField, displayField, imageField,
 *  searchFields) are module-declared on insert and pass through read-as-is.
 *  Consumers derive "naming rule" from `numberingField != null`.
 *
 *  `defaultSeriesPrefix` and `defaultSeriesLastIssued` are read-only
 *  enrichments surfaced by `listDoctypes` so the Doctype Admin list page
 *  can render the active prefix + most recent generated number at a glance.
 *  Resolution mirrors `findSeries()` in numbering.service.ts:
 *    - prefer the per-company default (`is_default = 1, company_id = X`)
 *    - fall back to the platform-default row (`company_id IS NULL`)
 *    - both null when no series is registered for the (module, doctype) pair
 *  `defaultSeriesLastIssued` is the prefix with the *most recent issued*
 *  counter applied (e.g. `INV-2026-00007` when `currentValue = 7`); null when
 *  the counter is still 0 (no number issued yet) or no series exists.
 *  Single-row reads (`getDoctypeDefault`, `rowToResolved`) leave both at null
 *  because the projection happens BEFORE numbering enrichment in the list
 *  path; consumers that ignore the fields are not affected (additive only). */
export interface ResolvedDoctype {
  id: string;
  moduleId: string;
  tableName: string;
  label: string;
  icon: string | null;
  features: DoctypeFeature[];
  tier: 0 | 1 | 2;
  kind: DoctypeKind;
  catalog: ResolvedDoctypeCatalogMetadata;
  numberingField: string | null;
  displayField: string | null;
  imageField: string | null;
  searchFields: string[] | null;
  columnLabels: Record<string, string> | null;
  lifecycle: DocumentLifecycleContract | null;
  defaultSeriesPrefix: string | null;
  defaultSeriesLastIssued: string | null;
}

/**
 * List every registered doctype with per-company overrides applied.
 *
 * Resolution semantics (mirrors ERPNext "Customize Form" override model):
 *   - For a given doctype `id`, prefer the row with `company_id = companyId`
 *     if one exists; otherwise fall back to the platform default row
 *     (`company_id IS NULL`).
 *   - If `companyId` is omitted, only platform defaults are returned. This
 *     makes the service safe to call from boot/admin scripts that don't
 *     have a tenant context.
 *
 * Ordering: `(moduleId ASC, label ASC)`. Stable ordering matters because
 * the Admin UI groups by module and the visual diff across reloads must
 * stay readable.
 *
 * Defense-in-depth:
 *   - `features` is run through `isDoctypeFeature` when parsed, so stale
 *     capability strings from an earlier app version don't leak into the
 *     UI. The DB CHECK(json_valid) guards structure; this guards values.
 *   - `tier` is narrowed back to `0 | 1 | 2` (with a safe fallback to 2);
 *     see the same treatment in `getDoctypeDefault`.
 *
 * We do the override merge in JS rather than SQL because:
 *   1. D1 lacks `DISTINCT ON` and the portable SQL (window function +
 *      self-join) is harder to read than a compact JS reducer for the
 *      catalog-sized dataset.
 *   2. The `.filter(isDoctypeFeature)` step has to happen in JS anyway.
 *   3. With the `(module_id)` and `(company_id, id)` indexes from the
 *      baseline migration, the 2-row-per-doctype worst case is fast.
 */
export async function listDoctypes(
  db: DrizzleD1Database,
  companyId?: string,
): Promise<ResolvedDoctype[]> {
  // Pull platform defaults + (optionally) this company's overrides in ONE
  // query. `company_id IS NULL OR company_id = :companyId` uses the
  // `idx_doctype_registry_company_id (company_id, id)` index for the
  // override leg and a full scan of the ~dozens-of-rows default set —
  // acceptable at our expected scale.
  const whereClause = companyId
    ? or(isNull(doctypeRegistry.companyId), eq(doctypeRegistry.companyId, companyId))
    : isNull(doctypeRegistry.companyId);

  const rows = await db
    .select()
    .from(doctypeRegistry)
    .where(whereClause);

  // Bucket by id, keeping the company override when present. Lifecycle is
  // module-owned metadata, so company override rows always inherit the
  // current platform-default lifecycle instead of shadowing it with NULL
  // or a stale copied JSON blob.
  const defaultsById = new Map<string, typeof rows[number]>();
  const overridesById = new Map<string, typeof rows[number]>();
  for (const row of rows) {
    if (row.companyId === null) defaultsById.set(row.id, row);
    else overridesById.set(row.id, row);
  }

  // Project each row via the shared helper so getDoctypeDefault and
  // listDoctypes agree on the exact shape (including the #365 metadata
  // fields). Fail-soft on malformed JSON is handled inside rowToResolved.
  const resolved: ResolvedDoctype[] = [];
  for (const defaultRow of defaultsById.values()) {
    const override = overridesById.get(defaultRow.id);
    const row = override
      ? {
          ...defaultRow,
          ...override,
          lifecycle: defaultRow.lifecycle,
          kind: defaultRow.kind,
          catalogMetadata: defaultRow.catalogMetadata,
        }
      : defaultRow;
    resolved.push(rowToResolved(row));
  }
  for (const override of overridesById.values()) {
    if (!defaultsById.has(override.id)) {
      resolved.push(rowToResolved(override));
    }
  }

  // Numbering enrichment (#422 follow-up) — surface the active default series
  // prefix per doctype so the Doctype Admin list page can render it without
  // an N+1 round-trip.
  //
  // ONE query covers every (module, doctype) in the result set:
  //   - filter to rows where `is_default = 1` for the relevant modules and
  //     doctypes — index `idx_numbering_series_doctype_company` plus the
  //     partial UNIQUE on `is_default = 1` keep this cheap.
  //   - filter to rows whose `company_id` is either NULL (platform default)
  //     or the requested `companyId` — same companyId-aware shape used for
  //     the registry query above.
  //
  // Resolution per (module, doctype): mirror `findSeries()` from
  // numbering.service.ts — prefer the per-company default; fall back to NULL
  // platform default. We loop over the small result set in JS for the same
  // reasons as the registry override merge above (D1 lacks DISTINCT ON, JS
  // is more readable for the catalog-sized dataset).
  if (resolved.length > 0) {
    const moduleIds = Array.from(new Set(resolved.map((d) => d.moduleId)));
    const doctypeIds = Array.from(new Set(resolved.map((d) => d.id)));
    const seriesRows: Array<{
      module: string;
      doctype: string;
      companyId: string | null;
      prefix: string;
      currentValue: number;
    }> = [];

    // D1 rejects very large prepared statements. After the catalog grew past
    // 100 doctypes, one giant `doctype IN (...)` broke `/core/doctypes`, so
    // keep each enrichment query comfortably below that bind-parameter limit.
    for (const doctypeBatch of chunkArray(
      doctypeIds,
      NUMBERING_SERIES_DOCTYPE_BATCH_SIZE,
    )) {
      const seriesWhere = companyId
        ? and(
            inArray(numberingSeries.module, moduleIds),
            inArray(numberingSeries.doctype, doctypeBatch),
            eq(numberingSeries.isDefault, 1),
            or(
              isNull(numberingSeries.companyId),
              eq(numberingSeries.companyId, companyId),
            ),
          )
        : and(
            inArray(numberingSeries.module, moduleIds),
            inArray(numberingSeries.doctype, doctypeBatch),
            eq(numberingSeries.isDefault, 1),
            isNull(numberingSeries.companyId),
          );

      const batchRows = await db
        .select({
          module: numberingSeries.module,
          doctype: numberingSeries.doctype,
          companyId: numberingSeries.companyId,
          prefix: numberingSeries.prefix,
          currentValue: numberingSeries.currentValue,
        })
        .from(numberingSeries)
        .where(seriesWhere);

      seriesRows.push(...batchRows);
    }

    // Bucket by `${module}:${doctype}`, preferring per-company over NULL.
    // The inArray pre-filter trims the candidate set to "default rows for
    // doctypes we care about", so the worst case is two rows per doctype.
    // Per-company row wins for BOTH prefix and currentValue — we don't want
    // to mix the NULL row's prefix with a stale platform-default counter.
    const seriesByKey = new Map<
      string,
      { prefix: string; currentValue: number; companyId: string | null }
    >();
    for (const row of seriesRows) {
      const key = `${row.module}:${row.doctype}`;
      const existing = seriesByKey.get(key);
      if (existing === undefined) {
        seriesByKey.set(key, {
          prefix: row.prefix,
          currentValue: row.currentValue,
          companyId: row.companyId,
        });
        continue;
      }
      // Prefer the per-company override over the platform default. We can't
      // tell which we already stored without re-querying, so re-check the
      // current row: only overwrite when this row is a per-company match.
      if (row.companyId !== null) {
        seriesByKey.set(key, {
          prefix: row.prefix,
          currentValue: row.currentValue,
          companyId: row.companyId,
        });
      }
    }

    for (const d of resolved) {
      const series = seriesByKey.get(`${d.moduleId}:${d.id}`);
      if (series === undefined) continue;
      d.defaultSeriesPrefix = series.prefix;
      // `defaultSeriesLastIssued` mirrors what was last issued by
      // `generateNextNumber()`. The counter increments BEFORE inserting
      // into core_numbering_series, so currentValue == the most recently
      // returned number. When the counter is still 0, no number has been
      // issued yet and we surface null so the UI shows "—" instead of a
      // misleading `INV-2026-00000`.
      if (series.currentValue > 0) {
        d.defaultSeriesLastIssued = previewPattern(series.prefix, {
          nextValue: series.currentValue,
        });
      }
    }
  }

  // Stable UI ordering — moduleId first (so module sections group together),
  // then label within each module. Using `localeCompare` so Thai/English
  // labels sort sensibly side by side.
  resolved.sort((a, b) => {
    const byModule = a.moduleId.localeCompare(b.moduleId);
    if (byModule !== 0) return byModule;
    return a.label.localeCompare(b.label);
  });

  return resolved;
}
